import {
  engagementStatusLabel,
  noticePeriodText,
  terminationChannelLabel,
  terminationKindLabel,
  terminationLastDay,
  type TerminationKind,
} from '@kanos/domain';
import { Button, DateField, SelectField, TextField, useToast } from '@kanos/ui';
import {
  formatDate,
  formatDateTime,
  meQuery,
  ProblemAlert,
  todayIso,
  useIdempotencyKey,
} from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { FileText, FileX2 } from 'lucide-react';
import { useState } from 'react';
import { useOpenDocument, useTerminate, type RunningContract } from './engagement-queries';
import { ContractSection as Section } from './engagement-withdrawal';

/**
 * The contract that applies (ADR-071 stage 6): its state in words, its documents - and, while in
 * force, notice through the portal (§ 312k BGB).
 */
export function RunningContractSection({
  caseId,
  contract,
}: {
  caseId: string;
  contract: RunningContract;
}) {
  const open = useOpenDocument(caseId);
  const { termination } = contract;
  return (
    <>
      <Section title="Ihr Vertrag">
        <p className="text-sm" role="status">
          {`Steuerberatungsvertrag mit ${contract.firmName}, Fassung ${String(contract.version)}: ${engagementStatusLabel[contract.status]}, in Kraft seit ${formatDate(contract.inForceFrom)}.`}
          {termination
            ? ` ${termination.kind ? terminationKindLabel[termination.kind] : terminationChannelLabel[termination.channel]} vom ${formatDateTime(termination.receivedAt)} – der Vertrag endet mit Ablauf des ${formatDate(termination.endsOn)}.`
            : ` Kündigungsfrist: ${noticePeriodText(contract.noticePeriod)}.`}
        </p>
        <ul className="grid gap-2">
          {contract.documents.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm">{d.title}</span>
              <Button
                variant="secondary"
                size="sm"
                isPending={open.isPending && open.variables.document === d.id}
                onPress={() => open.mutate({ document: d.id, version: contract.version })}
              >
                <FileText aria-hidden className="size-4" />
                PDF öffnen
              </Button>
            </li>
          ))}
        </ul>
        {open.isError && <ProblemAlert error={open.error} />}
      </Section>
      {contract.terminable && <Termination caseId={caseId} contract={contract} />}
    </>
  );
}

const KINDS = (['ordinary', 'for_cause'] as const).map((id) => ({
  id,
  label: terminationKindLabel[id],
}));

/**
 * Notice through the portal (§ 312k BGB): „Verträge hier kündigen“ always in sight while the
 * contract is in force, then the kind, the day, name and address for the confirmation, then
 * „jetzt kündigen“ - and the confirmation at once and by mail.
 */
function Termination({ caseId, contract }: { caseId: string; contract: RunningContract }) {
  const me = useQuery(meQuery);
  const terminate = useTerminate(caseId);
  const toast = useToast();
  const [idempotencyKey] = useIdempotencyKey();
  const [asking, setAsking] = useState(false);
  const [kind, setKind] = useState<TerminationKind>('ordinary');
  const [endsOn, setEndsOn] = useState<string | null>(null);
  const [name, setName] = useState(contract.clientName);
  const [email, setEmail] = useState(me.data?.email ?? '');
  const [reason, setReason] = useState('');
  const today = todayIso();
  const lastDay = terminationLastDay({
    receivedOn: today,
    kind,
    period: contract.noticePeriod,
    endsOn,
  });
  return (
    <Section title="Kündigung">
      {!asking ? (
        <Button variant="secondary" className="justify-self-start" onPress={() => setAsking(true)}>
          <FileX2 aria-hidden className="size-4" />
          Verträge hier kündigen
        </Button>
      ) : (
        <div className="grid gap-4">
          <SelectField
            label="Art der Kündigung"
            placeholder="Bitte auswählen"
            options={KINDS}
            value={kind}
            onChange={setKind}
          />
          <DateField
            label="Zu einem späteren Tag (freiwillig)"
            description={`Leer: zum frühestmöglichen Tag, dem ${formatDate(contract.earliestEnd)}.`}
            value={endsOn}
            onChange={setEndsOn}
            minValue={today}
          />
          <TextField
            label="Ihr Name"
            placeholder="z. B. Erika Mustermann"
            value={name}
            onChange={setName}
            isRequired
          />
          <TextField
            label="E-Mail-Adresse für die Bestätigung"
            placeholder="z. B. erika.mustermann@example.org"
            type="email"
            value={email}
            onChange={setEmail}
            isRequired
          />
          <TextField
            label="Grund (freiwillig, bei fristloser Kündigung)"
            placeholder="z. B. Vertrauensverhältnis gestört"
            value={reason}
            onChange={setReason}
          />
          <p className="text-sm" role="status">
            {lastDay
              ? `Ihr Vertrag endet mit Ablauf des ${formatDate(lastDay)}.`
              : 'Der gewählte Tag liegt vor dem Ende der Kündigungsfrist.'}
          </p>
          {terminate.isError && <ProblemAlert error={terminate.error} />}
          <div className="flex flex-wrap gap-2">
            <Button variant="quiet" onPress={() => setAsking(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              isDisabled={lastDay === null}
              isPending={terminate.isPending}
              onPress={() =>
                terminate.mutate(
                  {
                    body: {
                      version: contract.version,
                      kind,
                      endsOn,
                      name,
                      email,
                      reason: reason.trim() ? reason.trim() : null,
                    },
                    idempotencyKey,
                  },
                  { onSuccess: () => toast({ tone: 'success', title: 'Kündigung eingegangen' }) },
                )
              }
            >
              jetzt kündigen
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}
